import * as XLSX from "xlsx-js-style";

export const MAX_BULK_SPLIT_ROWS = 250;
const REFERENCE_TYPES = {
  iob: { header: "IOB Ref", key: "iobref", sheet: "IOB References", file: "Commission-Split-IOB-References-Template.xlsx" },
  policy: { header: "Policy Number", key: "policynumber", sheet: "Policy Numbers", file: "Commission-Split-Policy-Numbers-Template.xlsx" },
};

const normaliseHeader = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

export const normaliseIobNumber = (value) => {
  const rawValue = String(value ?? "").trim();
  const match = rawValue.match(/^(?:IOB)?(\d+)$/i);
  return match ? `IOB${match[1]}` : "";
};

const findHeader = (rows, referenceType) => {
  const expected = REFERENCE_TYPES[referenceType];
  for (let rowIndex = 0; rowIndex < Math.min(rows.length, 15); rowIndex += 1) {
    const headerRow = rows[rowIndex] || [];
    const referenceColumn = headerRow.findIndex(
      (value) => normaliseHeader(value) === expected.key,
    );
    if (referenceColumn === -1) continue;

    const populatedHeaders = headerRow.filter(
      (value) => String(value ?? "").trim() !== "",
    );
    if (populatedHeaders.length !== 1) {
      throw new Error(`The workbook must contain only one column headed "${expected.header}".`);
    }
    return { rowIndex, referenceColumn };
  }
  return null;
};

export const parseCommissionSplitWorkbook = (arrayBuffer, fileName = "", referenceType = "iob") => {
  const expected = REFERENCE_TYPES[referenceType];
  if (!expected) throw new Error("Choose an IOB reference or policy number upload.");
  if (!/\.(xlsx|xls)$/i.test(String(fileName))) {
    throw new Error("Please choose an .xlsx or .xls file.");
  }

  const workbook = XLSX.read(arrayBuffer, { type: "array", raw: true });
  let parsedSheet = null;

  for (const sheetName of workbook.SheetNames) {
    const sheetRows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
      header: 1,
      defval: "",
      raw: referenceType === "iob",
    });
    const header = findHeader(sheetRows, referenceType);
    if (!header) continue;

    const dataRows = sheetRows.slice(header.rowIndex + 1);
    if (dataRows.some((row) =>
      (row || []).some((value, columnIndex) =>
        columnIndex !== header.referenceColumn && String(value ?? "").trim() !== ""))) {
      throw new Error(`The workbook must contain only the "${expected.header}" column.`);
    }
    const rows = dataRows
      .map((row, index) => ({
        rowNumber: header.rowIndex + index + 2,
        rawReference: row?.[header.referenceColumn],
      }))
      .filter((row) => String(row.rawReference ?? "").trim());

    parsedSheet = { rows, sheetName };
    break;
  }

  if (!parsedSheet) {
    throw new Error(`The workbook must contain one column headed "${expected.header}".`);
  }
  if (!parsedSheet.rows.length) {
    throw new Error(`The workbook does not contain any ${referenceType === "iob" ? "IOB references" : "policy numbers"}.`);
  }
  if (parsedSheet.rows.length > MAX_BULK_SPLIT_ROWS) {
    throw new Error(`A bulk upload can contain no more than ${MAX_BULK_SPLIT_ROWS} rows.`);
  }

  const seenReferences = new Set();
  const rows = parsedSheet.rows.map((row) => {
    const iobNumber = referenceType === "iob" ? normaliseIobNumber(row.rawReference) : "";
    const policyNumber = referenceType === "policy" ? String(row.rawReference ?? "").trim() : "";
    const reference = referenceType === "iob" ? iobNumber : policyNumber.toUpperCase();
    const errors = [];

    if (!reference) errors.push(referenceType === "iob"
      ? "Enter a valid IOB reference, for example IOB81008096."
      : "Enter a policy number.");
    if (reference && seenReferences.has(reference)) {
      errors.push(`Duplicate ${referenceType === "iob" ? "IOB reference" : "policy number"} in this workbook.`);
    }
    if (reference) seenReferences.add(reference);

    return {
      rowNumber: row.rowNumber,
      iobNumber,
      policyNumber,
      referenceType,
      status: errors.length ? "invalid" : "ready",
      reason: errors.join(" "),
    };
  });

  const validRows = rows.filter((row) => row.status === "ready");
  const invalidRows = rows.filter((row) => row.status === "invalid");
  return {
    fileName,
    referenceType,
    sheetName: parsedSheet.sheetName,
    rows,
    validRows,
    invalidRows,
    totalRows: rows.length,
    validCount: validRows.length,
    invalidCount: invalidRows.length,
  };
};

export const downloadCommissionSplitTemplate = (referenceType = "iob") => {
  const expected = REFERENCE_TYPES[referenceType];
  if (!expected) throw new Error("Choose an IOB reference or policy number template.");
  const workbook = XLSX.utils.book_new();
  const templateRows = [[expected.header]];
  const templateSheet = XLSX.utils.aoa_to_sheet(templateRows);
  templateSheet["!cols"] = [{ wch: referenceType === "policy" ? 32 : 20 }];
  templateSheet["!rows"] = [{ hpt: 26 }];
  templateSheet["!autofilter"] = { ref: "A1:A1" };
  templateSheet["!freeze"] = {
    xSplit: 0,
    ySplit: 1,
    topLeftCell: "A2",
    activePane: "bottomLeft",
    state: "frozen",
  };
  templateSheet.A1.s = {
    fill: { patternType: "solid", fgColor: { rgb: "081E56" } },
    font: { bold: true, color: { rgb: "FFFFFF" } },
    alignment: { horizontal: "left", vertical: "center", indent: 1 },
    border: {
      top: { style: "thin", color: { rgb: "081E56" } },
      bottom: { style: "thin", color: { rgb: "081E56" } },
      left: { style: "thin", color: { rgb: "081E56" } },
      right: { style: "thin", color: { rgb: "081E56" } },
    },
  };
  XLSX.utils.book_append_sheet(workbook, templateSheet, expected.sheet);
  XLSX.writeFile(workbook, expected.file, {
    bookType: "xlsx",
    cellStyles: true,
  });
};
